import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CaretLeft, Plus, Copy, DeviceMobile, WaveTriangle, Phone, ChatCircleText,
  User, AndroidLogo, AppleLogo, DownloadSimple,
} from "@phosphor-icons/react";
import { Shell } from "@/components/Shell";
import { useApp } from "@/context/AppContext";

function PhoneFrame({ children }) {
  return (
    <div className="relative mx-auto w-full max-w-[280px]">
      <div className="rounded-[2.2rem] border-[3px] border-gray-800 bg-white p-2 shadow-[6px_6px_0_0_#1F2937]">
        <div className="rounded-[1.6rem] overflow-hidden border-2 border-gray-800" style={{ background: "var(--app-bg)" }}>
          <div className="flex justify-center pt-2">
            <span className="h-1.5 w-16 rounded-full bg-gray-800/70" />
          </div>
          <div className="p-3 min-h-[300px] sb-appbg">{children}</div>
        </div>
      </div>
    </div>
  );
}

function Marker({ n }) {
  return (
    <span className="inline-flex items-center justify-center h-7 w-7 shrink-0 rounded-full text-white font-heading text-sm border-2 border-gray-800 shadow-[2px_2px_0_0_#1F2937]"
      style={{ background: "var(--app-primary)" }}>{n}</span>
  );
}

// --- Mock screens for each step ---
function MockProfile() {
  return (
    <div className="space-y-2">
      <p className="font-heading font-semibold text-gray-900 text-sm">Mein Profil</p>
      <div className="sb-card p-2 flex items-center gap-2">
        <span className="h-9 w-9 rounded-full border-2 border-gray-800 flex items-center justify-center" style={{ background: "var(--app-border)" }}><User size={18} weight="bold" /></span>
        <div className="text-[11px] font-body leading-tight"><b>Lena Muster</b><br />Name & Avatar</div>
      </div>
      <div className="sb-card p-2 space-y-1">
        <p className="text-[10px] uppercase text-gray-500 font-body">Notfallkontakt</p>
        <div className="sb-input !py-1.5 !text-[11px]">Mama · +43 …</div>
      </div>
      <div style={{ outline: "3px dashed var(--app-strong)", outlineOffset: "3px", borderRadius: "1.25rem" }}>
        <div className="sb-btn py-2 text-[11px] font-medium text-white text-center" style={{ background: "var(--app-primary)" }}>+ Kontakt hinzufügen</div>
      </div>
    </div>
  );
}

function MockBuddies() {
  return (
    <div className="space-y-2 relative">
      <p className="font-heading font-semibold text-gray-900 text-sm">Safe Buddies</p>
      <div className="sb-card p-2 text-[11px] font-body">🎒 Schulrucksack</div>
      <div className="sb-card p-2 text-[11px] font-body opacity-60">🔑 Schlüssel</div>
      <div className="absolute bottom-0 right-0" style={{ outline: "3px dashed var(--app-strong)", outlineOffset: "4px", borderRadius: "9999px" }}>
        <span className="h-12 w-12 rounded-full border-2 border-gray-800 flex items-center justify-center text-white shadow-[2px_2px_0_0_#1F2937]" style={{ background: "var(--app-primary)" }}>
          <Plus size={26} weight="bold" />
        </span>
      </div>
    </div>
  );
}

function MockUrl() {
  return (
    <div className="space-y-2">
      <p className="font-heading font-semibold text-gray-900 text-sm">SafeBuddy erstellt! 🎉</p>
      <p className="text-[10px] uppercase text-gray-500 font-body">Deine SafeBuddy-URL</p>
      <div className="sb-input !py-2 !text-[10px] break-all">safebuddy.at/t/a8F3kP9</div>
      <div style={{ outline: "3px dashed var(--app-strong)", outlineOffset: "3px", borderRadius: "1.25rem" }}>
        <div className="sb-btn py-2 text-[11px] font-medium bg-white flex items-center justify-center gap-1.5">
          <Copy size={14} weight="bold" /> URL kopieren
        </div>
      </div>
    </div>
  );
}

function MockNfc() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 pt-4">
      <div className="relative sb-float">
        <DeviceMobile size={66} weight="duotone" style={{ color: "var(--app-strong)" }} />
        <WaveTriangle size={26} weight="bold" className="absolute -right-5 top-6 sb-wobble" style={{ color: "var(--app-primary)" }} />
      </div>
      <span className="h-12 w-20 rounded-lg border-2 border-gray-800 bg-white flex items-center justify-center text-[9px] font-body text-gray-500">NFC-Tag</span>
      <div style={{ outline: "3px dashed var(--app-strong)", outlineOffset: "3px", borderRadius: "1.25rem" }} className="w-full">
        <div className="sb-btn py-2 text-[11px] font-medium text-white flex items-center justify-center gap-1.5" style={{ background: "var(--app-primary)" }}>
          <DeviceMobile size={14} weight="bold" /> NFC-Tag jetzt beschreiben
        </div>
      </div>
    </div>
  );
}

function MockFinder() {
  return (
    <div className="space-y-2">
      <div className="sb-card p-2 flex items-center gap-2">
        <span className="h-9 w-9 rounded-full border-2 border-gray-800 flex items-center justify-center text-lg" style={{ background: "var(--app-border)" }}>🐻</span>
        <div className="text-[11px] font-body leading-tight"><b>Notfall-Info</b><br />Lena Muster</div>
      </div>
      <div className="sb-btn py-2 text-[11px] font-medium text-white flex items-center justify-center gap-1.5" style={{ background: "var(--app-primary)" }}>
        <Phone size={14} weight="bold" /> Jetzt anrufen
      </div>
      <div className="sb-btn py-2 text-[11px] font-medium bg-white flex items-center justify-center gap-1.5">
        <ChatCircleText size={14} weight="bold" /> Nachricht hinterlassen
      </div>
    </div>
  );
}

const MOCKS = [MockProfile, MockBuddies, MockUrl, MockNfc, MockFinder];

export default function Guide() {
  const { t, lang } = useApp();
  const navigate = useNavigate();

  const PLATFORM = {
    de: {
      androidTitle: "Android + Chrome: direkt beschreiben",
      android: [
        "Öffne 'Safe Buddies' und tippe auf 'SafeBuddy erstellen'.",
        "Fülle das kurze Formular aus und tippe auf 'Erstellen'. Die App kopiert die URL automatisch.",
        "Es erscheint 'Halte deinen SafeBuddy Tag ans Gerät'. Erlaube NFC, falls gefragt.",
        "Halte den NFC-Tag an die Rückseite deines Handys und warte kurz.",
        "Fertig! Du siehst 'Dein SafeBuddy wurde erfolgreich erstellt'.",
      ],
      iosTitle: "iPhone & andere Geräte: mit NFC Tools",
      ios: [
        "SafeBuddy erstellen – die App kopiert den Link automatisch in die Zwischenablage.",
        "Lade die kostenlose App 'NFC Tools' aus dem App Store (Button unten).",
        "Öffne NFC Tools → 'Schreiben' → 'Datensatz hinzufügen' → 'URL/URI' und füge den kopierten Link ein.",
        "Tippe 'Schreiben' und halte den NFC-Tag an dein Gerät.",
        "Bestätigung abwarten – fertig! Der Tag öffnet nun deine SafeBuddy-Seite.",
      ],
    },
    en: {
      androidTitle: "Android + Chrome: write directly",
      android: [
        "Open 'Safe Buddies' and tap 'Create SafeBuddy'.",
        "Fill the short form and tap 'Create'. The app copies the URL automatically.",
        "You'll see 'Hold your SafeBuddy tag to the device'. Allow NFC if prompted.",
        "Hold the NFC tag to the back of your phone and wait a moment.",
        "Done! You'll see 'Your SafeBuddy was created successfully'.",
      ],
      iosTitle: "iPhone & other devices: with NFC Tools",
      ios: [
        "Create a SafeBuddy – the app copies the link to your clipboard automatically.",
        "Download the free 'NFC Tools' app from the App Store (button below).",
        "Open NFC Tools → 'Write' → 'Add a record' → 'URL/URI' and paste the copied link.",
        "Tap 'Write' and hold the NFC tag to your device.",
        "Wait for confirmation – done! The tag now opens your SafeBuddy page.",
      ],
    },
  };
  const P = PLATFORM[lang] || PLATFORM.de;
  const storeUrl = /iphone|ipad|ipod/i.test(typeof navigator !== "undefined" ? navigator.userAgent : "")
    ? "https://apps.apple.com/app/nfc-tools/id1252962749"
    : "https://play.google.com/store/apps/details?id=com.wakdev.wdnfc";

  const STEP_DATA = {
    de: [
      { title: "SafeBuddy erstellen", text: "Erstelle in der App einen SafeBuddy und gib deinen Aktivierungscode ein." },
      { title: "Companion App öffnen", text: "Öffne danach die SafeBuddy Companion App auf deinem Handy." },
      { title: "NFC-Tag beschreiben", text: "Halte den NFC-Tag an dein Handy. Der Tag wird automatisch beschrieben." },
      { title: "Tag befestigen", text: "Befestige den fertigen Tag an deinem Gegenstand. Fertig!" },
    ],
    en: [
      { title: "Create a SafeBuddy", text: "Create a SafeBuddy in the app and enter your activation code." },
      { title: "Open the Companion app", text: "Then open the SafeBuddy Companion app on your phone." },
      { title: "Write the NFC tag", text: "Hold the NFC tag to your phone. The tag is written automatically." },
      { title: "Attach the tag", text: "Attach the finished tag to your item. Done!" },
    ],
  };
  const SD = STEP_DATA[lang] || STEP_DATA.de;
  const steps = SD.map((s, i) => ({
    n: i + 1,
    title: s.title,
    text: s.text,
    Mock: MOCKS[i],
  }));

  return (
    <Shell>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
        <div className="text-center">
          <h1 data-testid="guide-title" className="text-3xl font-heading font-semibold text-gray-900">{t("guideTitle")}</h1>
          <p className="font-body text-gray-600 mt-1">{t("guideIntro")}</p>
        </div>

        <div className="space-y-6">
          {steps.map(({ n, title, text, Mock }) => (
            <motion.div key={n} data-testid={`guide-step-${n}`}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.4 }}
              className="sb-card p-5">
              <div className="flex items-center gap-3 mb-3">
                <Marker n={n} />
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-body text-gray-400">{t("guideStepLabel")} {n}</p>
                  <h2 className="text-lg font-heading font-semibold text-gray-900 leading-tight">{title}</h2>
                </div>
              </div>

              <p className="font-body text-sm text-gray-700 leading-relaxed mb-4">{text}</p>

              <PhoneFrame><Mock /></PhoneFrame>
            </motion.div>
          ))}
        </div>

        <div className="space-y-4" data-testid="guide-platform">
          <div className="sb-card p-5" data-testid="guide-android">
            <h2 className="text-lg font-heading font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <AndroidLogo size={22} weight="fill" style={{ color: "var(--app-strong)" }} />{P.androidTitle}
            </h2>
            <ol className="list-decimal pl-5 space-y-1.5 font-body text-sm text-gray-800">
              {P.android.map((s, i) => (<li key={i}>{s}</li>))}
            </ol>
          </div>
          <div className="sb-card p-5" data-testid="guide-ios">
            <h2 className="text-lg font-heading font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <AppleLogo size={22} weight="fill" style={{ color: "var(--app-strong)" }} />{P.iosTitle}
            </h2>
            <ol className="list-decimal pl-5 space-y-1.5 font-body text-sm text-gray-800">
              {P.ios.map((s, i) => (<li key={i}>{s}</li>))}
            </ol>
            <a data-testid="guide-nfctools-link" href={storeUrl} target="_blank" rel="noreferrer"
              className="sb-btn w-full py-3 mt-4 bg-white font-medium flex items-center justify-center gap-2">
              <DownloadSimple size={18} weight="bold" />NFC Tools
            </a>
          </div>
        </div>
      </motion.div>
    </Shell>
  );
}
